/* SAEVENTEEN Arcade — Connect Four. Runs independently of the rest of the Shopify theme. */
(() => {
  'use strict';
  const ROWS = 6, COLS = 7, EMPTY = '0', X = 'x', O = 'o';
  const emptyBoard = () => Array(ROWS * COLS).fill(EMPTY);
  const getLegal = b => [3, 2, 4, 1, 5, 0, 6].filter(c => b[c] === EMPTY);
  function drop(b, c, side) {
    if (!Number.isInteger(c) || c < 0 || c >= COLS) return -1;
    for (let r = ROWS - 1; r >= 0; r--) {
      const i = r * COLS + c;
      if (b[i] === EMPTY) { b[i] = side; return i; }
    }
    return -1;
  }
  function winningLine(b, side) {
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
      if (b[r * COLS + c] !== side) continue;
      for (const [dr, dc] of [[0, 1], [1, 0], [1, 1], [1, -1]]) {
        const r3 = r + dr * 3, c3 = c + dc * 3;
        if (r3 < 0 || r3 >= ROWS || c3 < 0 || c3 >= COLS) continue;
        const ids = [0, 1, 2, 3].map(k => (r + dr * k) * COLS + c + dc * k);
        if (ids.every(i => b[i] === side)) return ids;
      }
    }
    return null;
  }
  function immediateMove(b, side) {
    for (const c of getLegal(b)) {
      const v = b.slice(); drop(v, c, side);
      if (winningLine(v, side)) return c;
    }
    return null;
  }
  function scoreBoard(b) {
    // Scoring is always from the CPU's perspective (O).
    let total = 0;
    for (let r = 0; r < ROWS; r++) if (b[r * COLS + 3] === O) total += 5;
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
      for (const [dr, dc] of [[0, 1], [1, 0], [1, 1], [1, -1]]) {
        if (r + dr * 3 >= ROWS || c + dc * 3 >= COLS || c + dc * 3 < 0) continue;
        let own = 0, other = 0;
        for (let k = 0; k < 4; k++) {
          const cell = b[(r + dr * k) * COLS + c + dc * k];
          own += Number(cell === O); other += Number(cell === X);
        }
        if (own && other) continue;
        if (own) total += [0, 1, 9, 75, 20000][own];
        if (other) total -= [0, 1, 10, 85, 20000][other];
      }
    }
    return total;
  }
  function minimax(b, depth, alpha, beta, maximizing, cache) {
    const terminalO = !!winningLine(b, O), terminalX = !!winningLine(b, X);
    if (terminalO) return 100000 + depth;
    if (terminalX) return -100000 - depth;
    const legal = getLegal(b);
    if (depth === 0 || legal.length === 0) return scoreBoard(b);
    const key = b.join('') + ':' + depth + ':' + maximizing;
    if (cache.has(key)) return cache.get(key);
    let best = maximizing ? -Infinity : Infinity, pruned = false;
    for (const col of legal) {
      const next = b.slice(); drop(next, col, maximizing ? O : X);
      const n = minimax(next, depth - 1, alpha, beta, !maximizing, cache);
      if (maximizing) { best = Math.max(best, n); alpha = Math.max(alpha, best); }
      else { best = Math.min(best, n); beta = Math.min(beta, best); }
      if (beta <= alpha) { pruned = true; break; }
    }
    // A pruned bound is not an exact minimax value: do not cache it.
    if (!pruned) cache.set(key, best);
    return best;
  }
  function aiMove(b, level) {
    const legal = getLegal(b);
    if (!legal.length) return null;
    if (level === 'easy') return legal[Math.floor(Math.random() * legal.length)];
    const winning = immediateMove(b, O);
    if (winning !== null) return winning;
    const blocking = immediateMove(b, X);
    if (blocking !== null) return blocking;
    if (level === 'medium') {
      let best = -Infinity, picked = legal[0];
      for (const c of legal) {
        const test = b.slice(); drop(test, c, O);
        const s = scoreBoard(test) + Math.random() * 30;
        if (s > best) { best = s; picked = c; }
      }
      return picked;
    }
    let chosen = legal[0], top = -Infinity;
    const cache = new Map();
    for (const col of legal) {
      const after = b.slice(); drop(after, col, O);
      // 6 additional plies, alpha/beta pruning with center-first ordering.
      const score = minimax(after, 6, -Infinity, Infinity, false, cache);
      if (score > top) { top = score; chosen = col; }
    }
    return chosen;
  }
  const loadJson = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key)) || fallback; } catch (_) { return fallback; } };
  const saveJson = (key, obj) => { try { localStorage.setItem(key, JSON.stringify(obj)); } catch (_) {} };
  function init(root) {
    if (root.dataset.saeInit) return;
    root.dataset.saeInit = '1';
    const one = sel => root.querySelector(sel);
    const all = sel => Array.from(root.querySelectorAll(sel));
    const ui = {
      board: one('[data-sae-board]'), status: one('[data-sae-status]'), gameLabel: one('[data-sae-game-label]'),
      xName: one('[data-sae-x-name]'), oName: one('[data-sae-o-name]'), xPanel: one('[data-sae-player-x]'), oPanel: one('[data-sae-player-o]'),
      aiSettings: one('[data-sae-ai-settings]'), onlineSettings: one('[data-sae-online-settings]'),
      name: one('[data-sae-name]'), code: one('[data-sae-code]'), room: one('[data-sae-room]'), roomCode: one('[data-sae-room-code]'),
      leave: one('[data-sae-leave]'), onlineHint: one('[data-sae-online-hint]'), reset: one('[data-sae-reset]')
    };
    const firstMode = ['ai','local','online'].includes(root.dataset.defaultMode) ? root.dataset.defaultMode : 'ai';
    const firstLevel = ['easy','medium','hard'].includes(root.dataset.defaultLevel) ? root.dataset.defaultLevel : 'medium';
    const state = { mode:firstMode, level:firstLevel, board:emptyBoard(), turn:X, winner:null, line:null, last:-1, calculating:false,
      online:null, playerId:null, supa:null, pollTimer:null, polling:false, requestId:0, stats:loadJson('sae_c4_stats_v1',{wins:0,losses:0,draws:0}), scored:loadJson('sae_c4_scored_v1',[]) };
    if (!Array.isArray(state.scored)) state.scored = [];
    if (!state.stats || typeof state.stats !== 'object') state.stats = {wins:0,losses:0,draws:0};
    const configured = root.dataset.onlineConfigured === 'true';
    const setStatus = msg => { ui.status.textContent = msg; };
    function showStats() {
      ['wins','losses','draws'].forEach(k => { const el=one('[data-sae-' + k + ']'); if(el) el.textContent = Number(state.stats[k]) || 0; });
    }
    function tally(result, id) {
      const scoreId = String(id);
      if (state.scored.includes(scoreId)) return;
      state.scored.push(scoreId);
      state.scored = state.scored.slice(-160);
      saveJson('sae_c4_scored_v1',state.scored);
      state.stats[result] = (Number(state.stats[result])||0)+1;
      saveJson('sae_c4_stats_v1',state.stats); showStats();
    }
    function isMyTurn() {
      if (state.winner || state.calculating) return false;
      if (state.mode === 'ai') return state.turn === X;
      if (state.mode === 'local') return true;
      const m = state.online;
      return !!(m && m.status === 'playing' && m.side === state.turn);
    }
    function render() {
      const m = state.online;
      root.dataset.saeYourSide = state.mode === 'online' && m && m.side === 'o' ? 'o' : 'x';
      ui.xName.textContent = state.mode === 'online' && m ? m.player_x_name : state.mode === 'ai' ? 'YOU' : 'PLAYER 1';
      ui.oName.textContent = state.mode === 'online' && m ? (m.player_o_name || 'WAITING...') : state.mode === 'ai' ? 'CPU' : 'PLAYER 2';
      const active = !state.winner && (state.mode === 'local' || state.mode === 'ai' || (m && m.status === 'playing'));
      ui.xPanel.classList.toggle('is-current',active && state.turn === X);
      ui.oPanel.classList.toggle('is-current',active && state.turn === O);
      ui.gameLabel.textContent = state.mode === 'ai' ? 'VS CPU — ' + state.level.toUpperCase() : state.mode === 'local' ? 'LOCAL — TWO PLAYERS' : m ? (m.mode === 'private' ? 'PRIVATE ROOM' : 'LIVE MATCH') : 'ONLINE MULTIPLAYER';
      ui.board.replaceChildren();
      state.board.forEach((side,i) => {
        const c = i % COLS, r = Math.floor(i / COLS);
        const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'sae-arcade__slot';
        if (side !== EMPTY) btn.classList.add(side === X ? 'is-x' : 'is-o');
        if (state.line && state.line.includes(i)) btn.classList.add('is-win');
        if (state.last === i) btn.classList.add('is-last');
        btn.setAttribute('aria-label','Column ' + (c+1) + ', row ' + (r+1) + ': ' + (side === EMPTY ? 'empty' : side === X ? 'red disc' : 'white disc'));
        btn.disabled = !isMyTurn() || state.board[c] !== EMPTY;
        btn.addEventListener('click', () => playColumn(c));
        ui.board.appendChild(btn);
      });
      if (state.mode === 'online' && m) {
        if (m.status === 'waiting') setStatus(m.mode === 'private' ? 'WAITING FOR FRIEND TO JOIN…' : 'SEARCHING FOR OPPONENT…');
        else if (m.status === 'cancelled') setStatus('ROOM CLOSED. START A NEW GAME.');
        else if (m.status === 'finished') {
          if (m.winner === 'draw') setStatus('DRAW — GOOD GAME');
          else if (m.reason === 'forfeit') setStatus(m.winner === m.side ? 'OPPONENT LEFT — YOU WIN' : 'GAME FORFEITED');
          else setStatus(m.winner === m.side ? 'YOU WON. 17 SHOTS.' : 'OPPONENT WON. RUN IT BACK.');
        } else setStatus(m.side === state.turn ? 'YOUR TURN — DROP A DISC' : 'OPPONENT IS THINKING…');
      } else if (state.winner) {
        setStatus(state.winner === 'draw' ? 'DRAW — NO MORE MOVES' : state.mode === 'ai' ? (state.winner === X ? 'YOU WIN. 17 SHOTS.' : 'CPU WINS — TRY AGAIN') : 'PLAYER ' + (state.winner === X ? '1' : '2') + ' WINS');
      } else setStatus(state.mode === 'ai' ? (state.calculating || state.turn === O ? 'CPU IS THINKING…' : 'YOUR TURN — DROP A DISC') : state.mode === 'local' ? 'PLAYER ' + (state.turn === X ? '1' : '2') + ' — YOUR TURN' : 'CHOOSE HOW TO PLAY ONLINE');
    }
    function resetLocal() {
      state.requestId++; state.board = emptyBoard();state.turn=X;state.winner=null;state.line=null;state.last=-1;state.calculating=false;render();
    }
    function finishLocal() {
      const line = winningLine(state.board,state.turn);
      if (line) { state.winner=state.turn;state.line=line; }
      else if (getLegal(state.board).length === 0) state.winner='draw';
      if (state.winner && state.mode==='ai') {
        tally(state.winner === 'draw' ? 'draws' : state.winner === X ? 'wins' : 'losses','local:' + Date.now() + ':' + Math.random());
      }
    }
    function playColumn(c) {
      if (!isMyTurn() || state.board[c] !== EMPTY) return;
      if (state.mode === 'online') { makeOnlineMove(c); return; }
      const pos = drop(state.board,c,state.turn);
      if (pos < 0) return;
      state.last=pos;finishLocal();
      if (!state.winner) state.turn = state.turn === X ? O : X;
      render();
      if (state.mode === 'ai' && !state.winner) {
        state.calculating = true;render();
        const version=++state.requestId;
        setTimeout(() => {
          if (version !== state.requestId || state.mode !== 'ai') return;
          const col = aiMove(state.board,state.level);
          if (col != null) {
            state.last=drop(state.board,col,O);finishLocal();
            if (!state.winner) state.turn=X;
          }
          state.calculating=false;render();
        },280);
      }
    }
    function onlineMessage(err) { setStatus('ONLINE ERROR: ' + ((err && err.message) || String(err)).slice(0,105).toUpperCase()); }
    async function connect() {
      if (!configured) throw new Error('SUPABASE SETUP REQUIRED — SEE README');
      if (!window.supabase || !window.supabase.createClient) throw new Error('ONLINE LIBRARY FAILED TO LOAD. TRY RELOADING.');
      if (!state.supa) {
        const url = root.dataset.supabaseUrl.trim(), key = root.dataset.supabaseKey.trim();
        if (!/^https:\/\/[a-z0-9.-]+\.supabase\.co\/?$/i.test(url)) throw new Error('INVALID SUPABASE PROJECT URL');
        state.supa=window.supabase.createClient(url,key,{auth:{persistSession:true,autoRefreshToken:true}});
      }
      const {data:sessionData,error:sessionError}=await state.supa.auth.getSession();
      if (sessionError) throw sessionError;
      if (!sessionData.session) {
        const {error}=await state.supa.auth.signInAnonymously(); if (error) throw error;
      }
      const {data:userInfo,error:userError}=await state.supa.auth.getUser();
      if (userError || !userInfo.user) throw userError || new Error('SIGN IN FAILED');
      state.playerId=userInfo.user.id;
      return state.supa;
    }
    async function rpc(functionName, args={}) {
      const sb=await connect();
      const {data,error}=await sb.rpc(functionName,args);
      if (error) throw error;
      if (!data || typeof data !== 'object') throw new Error('INVALID RESPONSE FROM GAME SERVER');
      return data;
    }
    function username() {
      const text=((ui.name && ui.name.value) || 'GUEST').trim().replace(/[<>\r\n]/g,'').slice(0,18) || 'GUEST';
      if(ui.name) ui.name.value=text;
      try { localStorage.setItem('sae_c4_username',text); } catch (_) {}
      return text;
    }
    function changeRoom(room) {
      state.online=room;
      if (room) {
        state.board=(typeof room.board==='string' && /^[0xo]{42}$/.test(room.board)) ? room.board.split('') : emptyBoard();
        state.turn=room.turn === O ? O : X;
        state.winner=room.winner || null;
        state.last=Number.isInteger(room.last_move)?room.last_move:-1;
        state.line = state.winner === X || state.winner === O ? winningLine(state.board,state.winner) : null;
        if(ui.leave) ui.leave.hidden=false;
        if(ui.room) ui.room.hidden = !(room.mode === 'private' && room.code);
        if (room.code) ui.roomCode.textContent=room.code;
        if (room.status === 'finished' && room.side && room.winner) {
          tally(room.winner === 'draw' ? 'draws' : room.winner === room.side ? 'wins' : 'losses','online:' + room.id);
        }
      } else {
        if(ui.room) ui.room.hidden=true; if(ui.leave) ui.leave.hidden=true;
        state.board=emptyBoard();state.turn=X;state.winner=null;state.line=null;state.last=-1;
      }
      render();
      root.dispatchEvent(new CustomEvent('sae:room-updated',{detail:{status:room ? room.status : 'closed', mode:room ? room.mode : null}}));
    }
    function stopPolling() { if (state.pollTimer) clearInterval(state.pollTimer);state.pollTimer=null;state.polling=false; }
    async function refresh() {
      if (state.polling || state.mode!=='online' || !state.online) return;
      state.polling=true;
      try {
        const current=state.online;
        if (!current) return;
        const response = current.status==='waiting' && current.mode==='public'
          ? await rpc('sae_c4_find_match',{p_name:username()})
          : await rpc('sae_c4_get',{p_match_id:current.id});
        if (state.mode==='online' && state.online && response.id === state.online.id) changeRoom(response);
      } catch (err) { onlineMessage(err); }
      finally { state.polling=false; }
    }
    function startPolling() {
      stopPolling();
      state.pollTimer=setInterval(() => {if (!document.hidden) refresh();},1500);
    }
    async function leaveOnline(silent=false) {
      const m=state.online;
      stopPolling();changeRoom(null);
      if (m && (m.status==='waiting' || m.status==='playing')) {
        try {await rpc('sae_c4_leave',{p_match_id:m.id});}
        catch(err) {if (!silent) onlineMessage(err);}
      }
    }
    async function beginOnline(kind) {
      if (state.mode !== 'online') return;
      one('[data-sae-find]').disabled=true;
      one('[data-sae-create]').disabled=true;
      one('[data-sae-join]').disabled=true;
      setStatus(kind==='public'?'FINDING YOUR MATCH…':'CONNECTING TO LOBBY…');
      try {
        if (state.online) await leaveOnline(true);
        let result;
        if (kind==='public') result=await rpc('sae_c4_find_match',{p_name:username()});
        if (kind==='private') result=await rpc('sae_c4_create_private',{p_name:username()});
        if (kind==='join') {
          const code=ui.code.value.trim().toUpperCase();
          if (!/^[0-9A-F]{8}$/.test(code)) throw new Error('ENTER A VALID 8 CHARACTER ROOM CODE');
          result=await rpc('sae_c4_join_private',{p_code:code,p_name:username()});
        }
        if (state.mode==='online') {changeRoom(result);startPolling();}
      } catch(err) {onlineMessage(err);}
      finally {one('[data-sae-find]').disabled=false;one('[data-sae-create]').disabled=false;one('[data-sae-join]').disabled=false;}
    }
    async function makeOnlineMove(col) {
      if (state.calculating) return;
      state.calculating=true; render();
      try {
        const current=state.online;
        const response=await rpc('sae_c4_play',{p_match_id:current.id,p_col:col});
        if (state.online && response.id===state.online.id) changeRoom(response);
      } catch(err) {onlineMessage(err);}
      finally {state.calculating=false;render();}
    }
    all('[data-sae-mode]').forEach(button=>button.addEventListener('click',async()=>{
      const newMode=button.dataset.saeMode;
      if (newMode===state.mode) return;
      state.requestId++;
      if (state.mode==='online') await leaveOnline(true);
      state.mode=newMode;
      all('[data-sae-mode]').forEach(b=>{b.classList.toggle('is-active',b===button);b.setAttribute('aria-pressed',String(b===button));});
      if(ui.aiSettings) ui.aiSettings.hidden=newMode!=='ai';
      if(ui.onlineSettings) ui.onlineSettings.hidden=newMode!=='online';
      resetLocal();
    }));
    all('[data-sae-level]').forEach(button=>button.addEventListener('click',()=>{
      state.level=button.dataset.saeLevel;
      all('[data-sae-level]').forEach(b=>{b.classList.toggle('is-active',b===button);b.setAttribute('aria-pressed',String(b===button));});
      resetLocal();
    }));
    if (ui.onlineSettings) {
    one('[data-sae-find]').addEventListener('click',()=>beginOnline('public'));
    one('[data-sae-create]').addEventListener('click',()=>beginOnline('private'));
    one('[data-sae-join]').addEventListener('click',()=>beginOnline('join'));
    ui.code.addEventListener('keydown',e=>{if(e.key==='Enter')beginOnline('join');});
    ui.leave.addEventListener('click',async()=>{await leaveOnline();setStatus('LEFT GAME — FIND ANOTHER MATCH');});
    one('[data-sae-copy]').addEventListener('click',async()=>{
      if (!state.online || !state.online.code) return;
      const url=new URL(window.location.href);url.searchParams.set('room',state.online.code);
      try {await navigator.clipboard.writeText(url.toString());one('[data-sae-copy]').textContent='COPIED ✓';setTimeout(()=>{one('[data-sae-copy]').textContent='COPY LINK';},1600);}
      catch(err){ui.code.value=state.online.code;setStatus('COPY THIS ROOM CODE TO INVITE A FRIEND');}
    });
    } // online controls enabled
    ui.reset.addEventListener('click',async()=>{
      if(state.mode==='online') {await leaveOnline(true);setStatus('CHOOSE A NEW ONLINE MATCH');}
      else resetLocal();
    });
    const invite=new URLSearchParams(window.location.search).get('room');
    if (ui.code && invite && /^[0-9a-f]{8}$/i.test(invite)) {
      ui.code.value=invite.toUpperCase();
      const onlineButton=one('[data-sae-mode="online"]'); if(onlineButton) onlineButton.click();
      if(ui.onlineHint) ui.onlineHint.textContent='INVITE DETECTED — ENTER A NAME, THEN PRESS JOIN.';
    }
    if(ui.name) try {ui.name.value=localStorage.getItem('sae_c4_username') || 'GUEST';} catch (_) {}
    root.saeArcade = {connect, rpc, state, leaveOnline};
    showStats();render();
  }
  function boot() {document.querySelectorAll('[data-sae-arcade]').forEach(init);}
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',boot);
  else boot();
  document.addEventListener('shopify:section:load',boot);
})();
